import React, { useCallback, useEffect, useRef, useState, Fragment } from 'react';
import { LoaderIcon, AlertCircleIcon } from 'lucide-react';
import { HeroSection } from '../components/HeroSection';
import { TrustSignals } from '../components/TrustSignals';
import { DegreeCategoryGrid } from '../components/DegreeCategoryGrid';
import { SEOContentSection } from '../components/SEOContentSection';
import { FAQSection } from '../components/FAQSection';
import { searchSchools, MatchedSchool } from '../services/matchingApi';
import { MatchedSchoolCard, DidYouKnowBanner } from '../components/MatchedSchoolCard';
import { HomeInlineLeadCapture } from '../components/HomeInlineLeadCapture';
import { ProgramContentSection } from '../components/ProgramContentSection';
import { useParams, useNavigate } from 'react-router-dom';
import { usePageMeta } from '../hooks/usePageMeta';
export function HomePage() {
  usePageMeta('OnlineCollegeGuide | Compare Accredited Online Degree Programs 2026', 'Compare accredited online colleges and universities. Get matched with degree programs that fit your career goals, schedule, and budget. No cost to students.', {
    ogImage: 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=1200&h=630&fit=crop&q=80',
    ogType: 'website'
  });
  const {
    areaStudy,
    program
  } = useParams<{
    areaStudy?: string;
    program?: string;
  }>();
  const navigate = useNavigate();
  const [matchedSchools, setMatchedSchools] = useState<MatchedSchool[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  // Track selected values for the content section
  const [selectedDegree, setSelectedDegree] = useState<string>('');
  const [selectedArea, setSelectedArea] = useState<string>('');
  const [selectedProgram, setSelectedProgram] = useState<string>('');
  // On narrow screens the hero and its widget fill the viewport, so results
  // rendered below it are invisible without scrolling — the visitor submits and
  // appears to get nothing back. Bring the matches into view once they arrive.
  const resultsRef = useRef<HTMLDivElement>(null);
  const pendingScrollRef = useRef(false);
  const fetchResults = useCallback(async (degreeInterest: string, areaStudyStr: string, mProgram: string) => {
    // Only search if mProgram (specialization) is selected
    if (!mProgram) {
      setHasSearched(false);
      return;
    }
    setSelectedDegree(degreeInterest);
    setSelectedArea(areaStudyStr);
    setSelectedProgram(mProgram);
    // Update URL if not already matching
    if (areaStudyStr && mProgram && (areaStudy !== areaStudyStr || program !== mProgram)) {
      navigate(`/degree/${areaStudyStr}/${mProgram}`, {
        replace: true
      });
    }
    pendingScrollRef.current = true;
    setIsLoading(true);
    setApiError(null);
    setHasSearched(true);
    const result = await searchSchools({
      degreeInterest: degreeInterest || undefined,
      areaStudy: areaStudyStr || undefined,
      mProgram: mProgram || undefined
    });
    if (result.success) {
      setMatchedSchools(result.schools);
    } else {
      setApiError('Unable to load school matches. Please try again.');
      setMatchedSchools([]);
    }
    setIsLoading(false);
  }, [navigate, areaStudy, program]);
  // On mount, if params exist, search
  useEffect(() => {
    if (areaStudy && program) {
      fetchResults('', areaStudy, program);
    }
  }, [areaStudy, program, fetchResults]);
  // Mobile only — on desktop the results already sit beside the fold.
  useEffect(() => {
    if (isLoading || !hasSearched || !pendingScrollRef.current) return;
    const target = resultsRef.current;
    if (!target || window.innerWidth >= 1024) {
      pendingScrollRef.current = false;
      return;
    }
    pendingScrollRef.current = false;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const raf = requestAnimationFrame(() => {
      target.scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'start'
      });
    });
    return () => cancelAnimationFrame(raf);
  }, [isLoading, hasSearched]);
  return <main className="flex-grow bg-light" data-id="element-2249" data-mp="35ifj1p8beb50">
      <HeroSection onSelectionChange={fetchResults} data-id="element-2250" data-mp="30ikrdjgpthiw" />
      <TrustSignals data-id="element-2251" data-mp="30jvstcmoud6u" />

      {hasSearched ? <div ref={resultsRef} id="school-matches" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 scroll-mt-20" data-id="element-2252" data-mp="30gq6g55g1pet">
          <div className="flex flex-col lg:flex-row gap-8" data-id="element-2253" data-mp="3t7hz4snavvxe">
            {/* Left Column: Results */}
            <div className="lg:w-2/3" data-id="element-2254" data-mp="3o7m4vl4eniv1">
              {/* Loading State */}
              {isLoading && <div className="flex flex-col items-center justify-center py-16 bg-white rounded-xl border border-gray-200" data-id="element-2255" data-mp="3k6tugojsr7x6">
                  <LoaderIcon className="w-8 h-8 text-navy animate-spin mb-4" data-id="element-2256" data-mp="2ct8hsihh77g2" />
                  <p className="text-gray-600 font-medium" data-id="element-2257" data-mp="28rn2yrldsj38">
                    Finding your best school matches...
                  </p>
                </div>}

              {/* Error State */}
              {!isLoading && apiError && <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-start gap-3" data-id="element-2258" data-mp="sbzed7nxys2f">
                  <AlertCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-2259" data-mp="x8zihd18dkqw" />
                  <div data-id="element-2260" data-mp="1210vx2a4xhii">
                    <p className="text-red-800 font-medium" data-id="element-2261" data-mp="122bumnoq3z2r">{apiError}</p>
                    <p className="text-red-600 text-sm mt-1" data-id="element-2262" data-mp="170n8ram1wvqs">
                      Try adjusting your search criteria or check back later.
                    </p>
                  </div>
                </div>}

              {/* Results */}
              {!isLoading && !apiError && matchedSchools.length > 0 && <div className="space-y-4" data-id="element-2263" data-mp="1kzvaaa5osfy5">
                  {matchedSchools.map((school, idx) => <Fragment key={(school as Record<string, unknown>).schoolid as string || idx} data-id="element-2264" data-mp="3hs58798467jl" data-mpi={idx}>
                      <MatchedSchoolCard school={school} index={idx} data-id="element-2265" data-mp="3mqq9zd30kza5" />
                      {idx === 0 && matchedSchools.length > 1 && <DidYouKnowBanner data-id="element-2266" data-mp="tvq0cnthhlws" />}
                    </Fragment>)}
                </div>}

              {/* Empty State */}
              {!isLoading && !apiError && matchedSchools.length === 0 && <div className="bg-white rounded-xl border border-gray-200 p-12 text-center" data-id="element-2267" data-mp="12xphrh8eus09">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4" data-id="element-2268" data-mp="xycyf6gr032f">
                    <AlertCircleIcon className="w-8 h-8 text-gray-400" data-id="element-2269" data-mp="1cukdjsdaueau" />
                  </div>
                  <h3 className="text-xl font-bold text-navy mb-2" data-id="element-2270" data-mp="17x2fix0tljhm">
                    No matches found
                  </h3>
                  <p className="text-gray-500 max-w-md mx-auto" data-id="element-2271" data-mp="1ln5vx7d0qiz5">
                    Try adjusting your degree level or program selection to find
                    more results.
                  </p>
                </div>}
            </div>

            {/* Right Column: Inline Lead Capture */}
            <div className="lg:w-1/3" data-id="element-2272" data-mp="1qo3915j68mts">
              <HomeInlineLeadCapture data-id="element-2273" data-mp="1qkxp04pegb4x" />
            </div>
          </div>

          {/* Dynamic Program Content Section */}
          {!isLoading && !apiError && selectedProgram && <ProgramContentSection degreeInterest={selectedDegree} areaOfStudy={selectedArea} specialization={selectedProgram} data-id="element-2274" data-mp="2f754kbwfxpy3" />}
        </div> : <>
          <DegreeCategoryGrid data-id="element-2275" data-mp="2597fjm6rbp4f" />
          <SEOContentSection data-id="element-2276" data-mp="25862jc66ksk2" />
          <FAQSection data-id="element-2277" data-mp="25bk6upyenbu5" />
        </>}
    </main>;
}